import axios from "axios";

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";

/**
 * Helper function to call SuiteCRM API
 */
const callSuiteCrmApi = async (method, data) => {
  const rest_data = JSON.stringify(data);
  
  // For set_note_attachment, use POST body to avoid URL length limits
  if (method === 'set_note_attachment') {
    const formData = new URLSearchParams();
    formData.append('method', method);
    formData.append('input_type', INPUT_TYPE);
    formData.append('response_type', RESPONSE_TYPE);
    formData.append('rest_data', rest_data);
    
    const response = await axios.post(API_URL, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  }
  
  // For other methods, use query string as before
  const fullUrl = `${API_URL}?method=${method}&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
  const response = await axios.post(fullUrl);
  return response.data;
};

/**
 * Create a note in SuiteCRM
 * @param {string} sessionId - Session ID
 * @param {string} relatedModuleName - Related module name (e.g., 'id_idcards')
 * @param {string} relatedModuleId - Related module ID
 * @param {string} name - Note name/title
 * @returns {Promise<string>} - Note ID
 */
export const createNote = async (sessionId, relatedModuleName, relatedModuleId, name = "Attachment") => {
  try {
    console.log("📝 Creating note for attachment...");
    
    const noteData = {
      session: sessionId,
      module_name: "Notes",
      name_value_list: [
        { name: "name", value: name },
        { name: "parent_type", value: relatedModuleName },
        { name: "parent_id", value: relatedModuleId },
      ],
    };

    const response = await callSuiteCrmApi("set_entry", noteData);
    console.log("✅ Note created with ID:", response.id);
    return response.id;
  } catch (error) {
    console.error("❌ Error creating note:", error);
    throw error;
  }
};

/**
 * Upload an image as a note attachment
 * @param {string} sessionId - Session ID
 * @param {string} noteId - Note ID to attach the file to
 * @param {string} base64Image - Base64 encoded image (with data:image/xxx;base64, prefix)
 * @param {string} filename - Filename for the attachment
 * @param {string} relatedModuleName - Related module name
 * @param {string} relatedModuleId - Related module ID
 * @returns {Promise<string>} - Note ID
 */
export const uploadImageAsNote = async (
  sessionId,
  noteId,
  base64Image,
  filename,
  relatedModuleName,
  relatedModuleId
) => {
  try {
    if (!base64Image || !base64Image.startsWith('data:')) {
      throw new Error("Invalid base64 image format");
    }

    console.log(`📤 Uploading image as note attachment: ${filename}`);

    // Extract base64 data and mime type
    const matches = base64Image.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
    if (!matches || matches.length !== 3) {
      throw new Error("Invalid base64 image format");
    }

    const mimeType = matches[1];
    const fileData = matches[2];

    const uploadData = {
      session: sessionId,
      note: {
        id: noteId,
        filename: filename,
        file: fileData,
        file_mime_type: mimeType,
        related_module_name: relatedModuleName,
        related_module_id: relatedModuleId,
      },
    };

    await callSuiteCrmApi("set_note_attachment", uploadData);
    console.log(`✅ Image uploaded successfully to note ${noteId}`);
    return noteId;
  } catch (error) {
    console.error("❌ Error uploading image as note:", error);
    throw error;
  }
};

/**
 * Get note attachment URL
 * @param {string} sessionId - Session ID
 * @param {string} noteId - Note ID
 * @returns {Promise<string>} - Attachment data (base64)
 */
export const getNoteAttachment = async (sessionId, noteId) => {
  try {
    if (!noteId) {
      console.log('⚠️  getNoteAttachment: noteId is empty/null');
      return null;
    }

    console.log(`━━━ getNoteAttachment START ━━━`);
    console.log(`📥 Fetching note attachment for note ID: "${noteId}"`);
    console.log(`   Note ID type: ${typeof noteId}`);
    console.log(`   Note ID length: ${noteId.length}`);
    console.log(`   Session ID: ${sessionId ? 'Present' : 'Missing'}`);

    const data = {
      session: sessionId,
      id: noteId,
    };

    console.log('📤 Calling SuiteCRM API with:', JSON.stringify(data));
    const response = await callSuiteCrmApi("get_note_attachment", data);
    console.log('📨 API Response received:', JSON.stringify(response).substring(0, 200));
    
    if (response && response.note_attachment) {
      const { filename, file, file_mime_type } = response.note_attachment;
      console.log(`✅ Note attachment found!`);
      console.log(`   Filename: ${filename}`);
      console.log(`   MIME type: ${file_mime_type}`);
      console.log(`   File data length: ${file ? file.length : 0} characters`);
      const base64Image = `data:${file_mime_type};base64,${file}`;
      console.log(`   Final base64 length: ${base64Image.length} characters`);
      console.log(`━━━ getNoteAttachment SUCCESS ━━━`);
      return base64Image;
    }

    console.log('⚠️  No note_attachment in response');
    console.log(`━━━ getNoteAttachment NO DATA ━━━`);
    return null;
  } catch (error) {
    console.error("❌ Error getting note attachment:", error);
    console.error("   Error details:", error.message);
    if (error.response) {
      console.error("   Response status:", error.response.status);
      console.error("   Response data:", JSON.stringify(error.response.data).substring(0, 200));
    }
    console.log(`━━━ getNoteAttachment ERROR ━━━`);
    return null;
  }
};
